import {useEffect, useRef} from 'react';

import {useLazySigner} from './api';

// Refresh a bit before the link actually expires, so a request that starts
// right before the deadline doesn't lose the race.
const REFRESH_BUFFER_MS = 60 * 1000;

/**
 * Keeps a mediaaccess signed_url alive for as long as the component using it
 * is mounted — fetches on mount, then silently re-fetches shortly before
 * each link's expires_at.
 */
export function useAutoRefreshingSignedUrl(file_ref: string) {
  const [trigger, result] = useLazySigner();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let cancelled = false;

    const fetchAndScheduleNext = async () => {
      try {
        const response = await trigger({file: file_ref}, false).unwrap();
        if (cancelled) {
          return;
        }

        const msUntilExpiry =
          new Date(response.expires_at).getTime() - Date.now();

        const delay = Math.max(msUntilExpiry - REFRESH_BUFFER_MS, 0);
        timeoutRef.current = setTimeout(fetchAndScheduleNext, delay);
      } catch {
        // Leave it to the manual refresh affordance rather than retry-looping
        // against a persistently failing request.
      }
    };

    fetchAndScheduleNext();

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [file_ref]);

  return {
    signed_url: result.data?.signed_url,
    isFetching: result.isFetching,
    isLoading: result.isLoading,
    refresh: () => trigger({file: file_ref}, false),
  };
}
